/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { preferencesService } from '../services/api';
import type { AppearancePreferences } from '../types';
import { formatINR } from '../components/console/ui';

export const APPEARANCE_PREFS_QUERY_KEY = ['appearancePreferences'] as const;

export const DEFAULT_APPEARANCE_PREFS: AppearancePreferences = {
  theme: 'dark',
  compact_mode: false,
  sidebar_collapsed: false,
  show_prices_per_minute: false,
};

/** Shared fetcher so the settings page and price displays use one cache entry. */
export const fetchAppearancePreferences = async (): Promise<AppearancePreferences> => {
  try {
    const response = await preferencesService.getAppearance();
    return { ...DEFAULT_APPEARANCE_PREFS, ...(response.data as Partial<AppearancePreferences>) };
  } catch {
    return DEFAULT_APPEARANCE_PREFS;
  }
};

/**
 * Reads the "Show prices per minute" appearance preference.
 *
 * `formatRate(perHour)` returns the hourly price, plus the per-minute price
 * when the preference is on - e.g. "₹120.00/hr · ₹2.00/min". Older backends
 * without the field behave as "off".
 */
export function usePriceUnit() {
  const { data } = useQuery({
    queryKey: APPEARANCE_PREFS_QUERY_KEY,
    queryFn: fetchAppearancePreferences,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const perMinute = !!data?.show_prices_per_minute;

  const formatRate = useCallback(
    (perHour: number | null | undefined, currency = 'INR', digits = 2) => {
      const hourly = `${formatINR(perHour, currency, digits)}/hr`;
      if (!perMinute) return hourly;
      return `${hourly} · ${formatINR(Number(perHour ?? 0) / 60, currency, Math.max(digits, 2))}/min`;
    },
    [perMinute],
  );

  return { perMinute, formatRate };
}

export default usePriceUnit;
